import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUser, FiBook, FiClock, FiUsers, FiTarget, FiTrendingUp, FiMapPin, FiDollarSign, FiAward, FiCheck, FiInfo } = FiIcons;

const EnhancedBasicInfo = ({ formData, updateFormData, setCurrentStep }) => {
  const navigate = useNavigate();
  const [localData, setLocalData] = useState({
    name: formData.name || '',
    subject: formData.subject || '',
    experience: formData.experience || '',
    currentStudents: formData.currentStudents || { active: '', notActive: '' },
    teachingMethod: formData.teachingMethod || '',
    targetAudience: formData.targetAudience || '',
    customTargetAudience: formData.customTargetAudience || '',
    location: formData.location || '',
    country: formData.country || '',
    currentRevenue: formData.currentRevenue || '',
    targetRevenue: formData.targetRevenue || '',
    certifications: formData.certifications || '',
    competition: formData.competition || '',
    marketingChannels: formData.marketingChannels || [],
    marketingBudget: formData.marketingBudget || '',
    timeCommitment: formData.timeCommitment || '',
    estimatedLeads: formData.estimatedLeads || '',
    estimatedProspects: formData.estimatedProspects || ''
  });
  
  const [showTooltip, setShowTooltip] = useState(null);

  const teachingMethods = [
    'One-on-one tutoring',
    'Group classes',
    'Online courses',
    'Workshops',
    'Hybrid (online + in-person)',
    'Corporate training',
    'Coaching/Mentoring',
    'Other'
  ];

  const targetAudiences = [
    'Elementary students',
    'Middle school students',
    'High school students',
    'College students',
    'Adult learners',
    'Professionals',
    'Seniors',
    'Parents of young learners',
    'Other Educators',
    'Mixed age groups'
  ];

  const marketingChannelOptions = [
    'Social Media (Instagram, Facebook, LinkedIn)',
    'Website/Blog',
    'Email Marketing',
    'YouTube/Video Content',
    'Local Community Events',
    'Referrals/Word of Mouth',
    'Online Marketplaces',
    'Paid Advertising',
    'Content Marketing',
    'Networking Groups'
  ];

  const numberRanges = [
    'Not sure',
    '0-50',
    '50-100',
    '100-200',
    '200-500',
    '500+'
  ];

  const tooltips = {
    name: {
      title: "Your Name",
      description: "Enter your full name as you'd like it to appear on your marketing materials.",
      example: "Example: Sarah Johnson"
    },
    subject: {
      title: "What do you teach or tutor?",
      description: "Be specific about your subject area and any specializations.",
      example: "Examples: Advanced Mathematics, Piano for Beginners, Web Development, Business English"
    },
    experience: {
      title: "Years of Teaching Experience",
      description: "Include both formal teaching and informal tutoring/coaching experience.",
      example: "Count all years where you've taught, tutored, or coached others"
    },
    currentStudents: {
      title: "Current Number of Students",
      description: "Track both active and inactive students to understand your client base.",
      example: "Active: Currently enrolled/paying students, Not Active: Past students who might return"
    },
    estimatedLeads: {
      title: "Estimated Number of Leads",
      description: "A lead is someone who has shown initial interest in your product or service (e.g., email subscribers, social media followers).",
      example: "Examples: Email list subscribers, Instagram followers, Facebook page likes"
    },
    estimatedProspects: {
      title: "Estimated Number of Prospects",
      description: "A prospect is a qualified lead who has been vetted and deemed a good fit for your offerings.",
      example: "Examples: People who have engaged with your content, requested more information, or participated in a free session"
    },
    teachingMethod: {
      title: "Primary Teaching Method",
      description: "How do you primarily deliver your teaching services?",
      example: "Choose the method you use most frequently, even if you use multiple methods"
    },
    targetAudience: {
      title: "Target Audience",
      description: "Who are your ideal students? Consider age, skill level, and learning goals.",
      example: "Examples: High school students preparing for SAT, Adults learning Spanish for travel"
    },
    location: {
      title: "Location",
      description: "Where are you based or where do you primarily serve students?",
      example: "City and state/province, or 'Online' if you teach remotely"
    },
    country: {
      title: "Country",
      description: "Your country of residence helps us provide relevant marketing strategies.",
      example: "Example: United States, Canada, United Kingdom, Australia"
    },
    currentRevenue: {
      title: "Current Monthly Revenue",
      description: "Your current monthly income from teaching activities.",
      example: "Include all teaching-related income: tutoring, courses, workshops, etc."
    },
    targetRevenue: {
      title: "Target Monthly Revenue",
      description: "Your realistic monthly income goal from teaching.",
      example: "Set an achievable goal based on your market and time commitment"
    },
    certifications: {
      title: "Certifications & Qualifications",
      description: "List any degrees, certifications, or special training that enhances your credibility.",
      example: "Examples: Master's in Education, TEFL Certification, Professional Music License"
    },
    marketingChannels: {
      title: "Current Marketing Channels",
      description: "How do you currently find new students or promote your teaching services?",
      example: "Select all methods you currently use, even if infrequently"
    },
    marketingBudget: {
      title: "Monthly Marketing Budget",
      description: "How much can you invest monthly in marketing your teaching business?",
      example: "Include advertising costs, online teacher page, getting listed in tutors directories, etc."
    },
    timeCommitment: {
      title: "Weekly Time Commitment",
      description: "How many hours per week can you dedicate to marketing activities?",
      example: "This doesn't include teaching time - only marketing and business development"
    }
  };

  const handleInputChange = (field, value) => {
    setLocalData(prev => ({ ...prev, [field]: value }));
  };

  const handleStudentCountChange = (field, value) => {
    setLocalData(prev => ({
      ...prev,
      currentStudents: {
        ...prev.currentStudents,
        [field]: value
      }
    }));
  };

  const handleMarketingChannelToggle = (channel) => {
    setLocalData(prev => ({
      ...prev,
      marketingChannels: prev.marketingChannels.includes(channel)
        ? prev.marketingChannels.filter(c => c !== channel)
        : [...prev.marketingChannels, channel]
    }));
  };

  const handleTargetAudienceChange = (value) => {
    setLocalData(prev => ({
      ...prev,
      targetAudience: value,
      customTargetAudience: value === 'Other' ? prev.customTargetAudience : ''
    }));
  };

  const handleNext = () => {
    updateFormData(localData);
    setCurrentStep(2);
    navigate('/niche');
  };

  const handlePrevious = () => {
    setCurrentStep(0);
    navigate('/');
  };

  const isFormValid = localData.name && localData.subject && localData.experience && localData.teachingMethod && (localData.targetAudience || localData.customTargetAudience);

  const Tooltip = ({ fieldKey, children }) => {
    const tooltip = tooltips[fieldKey];
    return (
      <div className="relative">
        {children}
        <button
          type="button"
          onMouseEnter={() => setShowTooltip(fieldKey)}
          onMouseLeave={() => setShowTooltip(null)}
          className="ml-2 text-gray-400 hover:text-blue-600 transition-colors"
        >
          <SafeIcon icon={FiInfo} className="text-sm" />
        </button>
        {showTooltip === fieldKey && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute z-50 w-80 p-3 bg-gray-900 text-white rounded-lg shadow-lg text-sm"
            style={{ bottom: '100%', marginBottom: '8px', left: '50%', transform: 'translateX(-50%)' }}
          >
            <div className="font-semibold mb-1">{tooltip.title}</div>
            <div className="mb-2">{tooltip.description}</div>
            <div className="text-gray-300 text-xs italic">{tooltip.example}</div>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
              <div className="border-4 border-transparent border-t-gray-900"></div>
            </div>
          </motion.div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8"
      >
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <SafeIcon icon={FiUser} className="text-2xl text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Tell Us About Your Teaching Business</h2>
          <p className="text-gray-600">Let's gather comprehensive information about your teaching practice</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Name */}
            <div>
              <Tooltip fieldKey="name">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Name *
                </label>
              </Tooltip>
              <input
                type="text"
                value={localData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter your full name"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>

            {/* Subject */}
            <div>
              <Tooltip fieldKey="subject">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  What do you teach or tutor? *
                </label>
              </Tooltip>
              <input
                type="text"
                value={localData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                placeholder="e.g., Mathematics, English, Piano, Web Development, etc."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>

            {/* Experience */}
            <div>
              <Tooltip fieldKey="experience">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Years of Teaching Experience *
                </label>
              </Tooltip>
              <select
                value={localData.experience}
                onChange={(e) => handleInputChange('experience', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">Select your experience level</option>
                <option value="Less than 1 year">Less than 1 year</option>
                <option value="1-2 years">1-2 years</option>
                <option value="3-5 years">3-5 years</option>
                <option value="6-10 years">6-10 years</option>
                <option value="More than 10 years">More than 10 years</option>
              </select>
            </div>

            {/* Current Students */}
            <div>
              <Tooltip fieldKey="currentStudents">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Current Number of Students
                </label>
              </Tooltip>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Active Students</label>
                  <input
                    type="number"
                    value={localData.currentStudents?.active || ''}
                    onChange={(e) => handleStudentCountChange('active', e.target.value)}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Not Active Students</label>
                  <input
                    type="number"
                    value={localData.currentStudents?.notActive || ''}
                    onChange={(e) => handleStudentCountChange('notActive', e.target.value)}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Estimated Leads */}
            <div>
              <Tooltip fieldKey="estimatedLeads">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Estimated Number of Leads
                </label>
              </Tooltip>
              <select
                value={localData.estimatedLeads}
                onChange={(e) => handleInputChange('estimatedLeads', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">Select range</option>
                {numberRanges.map(range => (
                  <option key={range} value={range}>{range}</option>
                ))}
              </select>
            </div>

            {/* Estimated Prospects */}
            <div>
              <Tooltip fieldKey="estimatedProspects">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Estimated Number of Prospects
                </label>
              </Tooltip>
              <select
                value={localData.estimatedProspects}
                onChange={(e) => handleInputChange('estimatedProspects', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">Select range</option>
                {numberRanges.map(range => (
                  <option key={range} value={range}>{range}</option>
                ))}
              </select>
            </div>

            {/* Teaching Method */}
            <div>
              <Tooltip fieldKey="teachingMethod">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Primary Teaching Method *
                </label>
              </Tooltip>
              <select
                value={localData.teachingMethod}
                onChange={(e) => handleInputChange('teachingMethod', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">Select your teaching method</option>
                {teachingMethods.map(method => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
            </div>

            {/* Target Audience */}
            <div>
              <Tooltip fieldKey="targetAudience">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Target Audience *
                </label>
              </Tooltip>
              <select
                value={localData.targetAudience}
                onChange={(e) => handleTargetAudienceChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">Select your target audience</option>
                {targetAudiences.map(audience => (
                  <option key={audience} value={audience}>{audience}</option>
                ))}
              </select>
              {localData.targetAudience === 'Other' && (
                <input
                  type="text"
                  value={localData.customTargetAudience}
                  onChange={(e) => handleInputChange('customTargetAudience', e.target.value)}
                  placeholder="Please specify your target audience..."
                  className="w-full mt-2 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Location */}
            <div>
              <Tooltip fieldKey="location">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiMapPin} className="inline mr-1" /> Location (City/Online)
                </label>
              </Tooltip>
              <input
                type="text"
                value={localData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="e.g., New York, NY or Online"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>

            {/* Country */}
            <div>
              <Tooltip fieldKey="country">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Country
                </label>
              </Tooltip>
              <input
                type="text"
                value={localData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                placeholder="e.g., United States, Canada, UK"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>

            {/* Current Revenue */}
            <div>
              <Tooltip fieldKey="currentRevenue">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiDollarSign} className="inline mr-1" /> Current Monthly Revenue (Optional)
                </label>
              </Tooltip>
              <select
                value={localData.currentRevenue}
                onChange={(e) => handleInputChange('currentRevenue', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">Select range</option>
                <option value="$0-$500">$0-$500</option>
                <option value="$500-$1,000">$500-$1,000</option>
                <option value="$1,000-$2,500">$1,000-$2,500</option>
                <option value="$2,500-$5,000">$2,500-$5,000</option>
                <option value="$5,000-$10,000">$5,000-$10,000</option>
                <option value="Over $10,000">Over $10,000</option>
              </select>
            </div>

            {/* Target Revenue */}
            <div>
              <Tooltip fieldKey="targetRevenue">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiTrendingUp} className="inline mr-1" /> Target Monthly Revenue (Optional)
                </label>
              </Tooltip>
              <select
                value={localData.targetRevenue}
                onChange={(e) => handleInputChange('targetRevenue', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">Select target</option>
                <option value="$1,000">$1,000</option>
                <option value="$2,500">$2,500</option>
                <option value="$5,000">$5,000</option>
                <option value="$7,500">$7,500</option>
                <option value="$10,000">$10,000</option>
                <option value="$15,000">$15,000</option>
                <option value="$20,000+">$20,000+</option>
              </select>
            </div>

            {/* Certifications */}
            <div>
              <Tooltip fieldKey="certifications">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiAward} className="inline mr-1" /> Certifications & Qualifications
                </label>
              </Tooltip>
              <textarea
                value={localData.certifications}
                onChange={(e) => handleInputChange('certifications', e.target.value)}
                placeholder="List your relevant certifications, degrees, or qualifications"
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
              />
            </div>

            {/* Marketing Channels */}
            <div>
              <Tooltip fieldKey="marketingChannels">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Current Marketing Channels
                </label>
              </Tooltip>
              <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {marketingChannelOptions.map(channel => (
                  <label key={channel} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <input
                      type="checkbox"
                      checked={localData.marketingChannels.includes(channel)}
                      onChange={() => handleMarketingChannelToggle(channel)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{channel}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Marketing Budget */}
            <div>
              <Tooltip fieldKey="marketingBudget">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Monthly Marketing Budget
                </label>
              </Tooltip>
              <select
                value={localData.marketingBudget}
                onChange={(e) => handleInputChange('marketingBudget', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">Select budget range</option>
                <option value="No budget">No budget</option>
                <option value="$0-$50">$0-$50</option>
                <option value="$50-$100">$50-$100</option>
                <option value="$100-$300">$100-$300</option>
                <option value="$300-$500">$300-$500</option>
                <option value="$500-$1,000">$500-$1,000</option>
                <option value="Over $1,000">Over $1,000</option>
              </select>
            </div>

            {/* Time Commitment */}
            <div>
              <Tooltip fieldKey="timeCommitment">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <SafeIcon icon={FiClock} className="inline mr-1" /> Weekly Time Commitment for Marketing
                </label>
              </Tooltip>
              <select
                value={localData.timeCommitment}
                onChange={(e) => handleInputChange('timeCommitment', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="">Select time commitment</option>
                <option value="1-2 hours">1-2 hours</option>
                <option value="3-5 hours">3-5 hours</option>
                <option value="6-10 hours">6-10 hours</option>
                <option value="11-15 hours">11-15 hours</option>
                <option value="20+ hours">20+ hours</option>
              </select>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-8 flex justify-between">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handlePrevious}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all"
          >
            Previous
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleNext}
            disabled={!isFormValid}
            className={`px-8 py-3 rounded-lg font-semibold transition-all ${
              isFormValid
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Next: Define Your Niche
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default EnhancedBasicInfo;